import React, { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle2, Lightbulb, Loader2, ChevronRight, Zap, AlertCircle, RefreshCw, SkipForward, ArrowLeft } from 'lucide-react';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { collegeJourneyService } from '../../../lib/college-journey-service';
import EssayResultsDisplay from './EssayResultsDisplay';

export default function HiddenCompetenciesInterview({ onComplete, assessmentData }) {
  const { currentUser } = useFamilyContext();
  const [phase, setPhase] = useState('intro'); // 'intro', 'questioning', 'processing', 'results', 'error'
  const [responses, setResponses] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [insights, setInsights] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [savedResponses, setSavedResponses] = useState(null);
  const [showAutoSave, setShowAutoSave] = useState(false);

  const questions = [
    {
      id: 'go_to_person',
      question: "What's something random you've somehow become the go-to person for amongst your friends and/or family?",
      placeholder: "Think about anything people ask for your help with - tech issues, advice, specific skills..."
    },
    {
      id: 'taught_adult',
      question: "Tell me about a time you had to explain or teach something to an adult.",
      placeholder: "Could be technology, a skill, a concept, or helping them understand something new..."
    },
    {
      id: 'surprised_reaction',
      question: "Describe the last time someone said 'Wait, how do you know that?' or 'Where did you learn to do that?'",
      placeholder: "Think about moments when your knowledge or skills surprised someone..."
    },
    {
      id: 'unrelated_knowledge',
      question: `Tell me three things you know a lot about that's totally UNRELATED to ${assessmentData?.primaryFocus || 'your main academic interests'}.`,
      placeholder: "List any topics, hobbies, or areas where you have surprising depth of knowledge..."
    },
    {
      id: 'solved_unprompted',
      question: "Tell me about something you fixed, solved, or figured out in the last few months that nobody asked you to handle.",
      placeholder: "Think about problems you tackled on your own initiative..."
    },
    {
      id: 'should_teach_in_school',
      question: "What's something you've taught yourself that you think they ought to teach in school?",
      placeholder: "Consider practical skills, knowledge, or abilities you've developed independently..."
    }
  ];

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const wordCount = currentAnswer.trim().split(/\s+/).filter(Boolean).length;
  const hasMinimumDetail = wordCount >= 50;

  // Auto-save indicator effect
  useEffect(() => {
    if (currentAnswer.length > 0) {
      setShowAutoSave(true);
      const timer = setTimeout(() => setShowAutoSave(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [currentAnswer]);

  const startInterview = () => {
    setPhase('questioning');
  };

  const submitAnswer = () => {
    const newResponses = {
      ...responses,
      [currentQuestion.id]: currentAnswer.trim() || ''
    };
    setResponses(newResponses);

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer('');
    } else {
      finishInterview(newResponses);
    }
  };

  const skipQuestion = () => {
    const newResponses = {
      ...responses,
      [currentQuestion.id]: ''
    };
    setResponses(newResponses);

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer('');
    } else {
      finishInterview(newResponses);
    }
  };

  const finishInterview = async (finalResponses) => {
    // Count non-empty responses
    const answeredQuestions = Object.values(finalResponses).filter(response => response && response.trim().length > 0);
    
    if (answeredQuestions.length < 4) {
      alert(`Please answer at least 4 questions to complete the interview. You've answered ${answeredQuestions.length} questions.`);
      return;
    }

    setPhase('processing');
    setIsProcessing(true);

    try {
      // Generate AI insights from responses
      const insightData = await generateInsights(finalResponses);
      setInsights(insightData);
      
      // Save the results to the database
      try {
        console.log('💾 Saving interview results to database...');
        const saveResult = await collegeJourneyService.saveInterviewResults(
          currentUser?.id,
          'skills_discovery',
          insightData
        );
        console.log('💾 Interview results saved:', saveResult ? 'Success' : 'Failed (table may not exist)');
      } catch (saveError) {
        console.error('Failed to save interview results:', saveError);
        // Don't block the UI, continue showing results even if save fails
      }
      
      setPhase('results');
      
      // Notify parent component
      if (onComplete) {
        onComplete({
          type: 'skills_discovery',
          responses: finalResponses,
          insights: insightData
        });
      }
    } catch (error) {
      console.error('Error generating insights:', error);
      
      // Save responses for retry
      setSavedResponses(finalResponses);
      
      // Check if it's a JSON parsing error from our API
      const isParsingError = error.message?.includes('parse') || 
                            error.message?.includes('JSON') ||
                            (error.shouldRetry);
      
      // Show retry option instead of fallback
      setInsights({
        error: true,
        message: error.isOverloaded 
          ? "The AI service is temporarily busy. Please try again in a moment."
          : isParsingError
          ? "The AI response format was unexpected. Please try again."
          : "Failed to generate insights. Please try again.",
        canRetry: true
      });
      setPhase('error');
    }

    setIsProcessing(false);
  };

  const generateInsights = async (interviewResponses) => {
    try {
      // Call new unified essay-ideas API
      const response = await fetch('/api/essay-ideas/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          interviewType: 'hidden_competencies',
          studentName: currentUser?.name || currentUser?.displayName || 'this student',
          responses: interviewResponses,
          userId: currentUser?.id,
          context: {
            personality: assessmentData?.personality,
            assessmentCompleted: !!assessmentData
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Check if it's an overloaded error
        if (errorData.isOverloaded || response.status === 503) {
          const error = new Error('Service temporarily overloaded');
          error.isOverloaded = true;
          throw error;
        }

        throw new Error(errorData.error || 'Failed to generate insights');
      }

      const data = await response.json();

      // New API returns flat structure: { essayTopics, magneticHook, surpriseInsight, ... }
      return data;
    } catch (error) {
      console.error('Error calling insights API:', error);
      
      // Add specific error properties for better handling
      error.isNetworkError = error.message.includes('fetch') || error.message.includes('network');
      error.isOverloaded = error.isOverloaded || error.message.includes('overloaded');
      
      throw error;
    }
  };

  const retryAnalysis = async () => {
    setPhase('processing');
    setIsProcessing(true);
    setRetryCount(prev => prev + 1);
    
    try {
      // Use saved responses for retry
      const responsesToAnalyze = savedResponses || responses;
      const insightData = await generateInsights(responsesToAnalyze);
      setInsights(insightData);
      
      // Save the results to the database
      try {
        console.log('💾 Saving retry interview results to database...');
        const saveResult = await collegeJourneyService.saveInterviewResults(
          currentUser?.id,
          'skills_discovery',
          insightData
        );
        console.log('💾 Retry interview results saved:', saveResult ? 'Success' : 'Failed (table may not exist)');
      } catch (saveError) {
        console.error('Failed to save retry interview results:', saveError);
        // Don't block the UI, continue showing results even if save fails
      }
      
      setPhase('results');
      
      // Notify parent component on successful retry
      if (onComplete) {
        onComplete({
          type: 'skills_discovery',
          responses: responsesToAnalyze,
          insights: insightData
        });
      }
    } catch (error) {
      console.error(`Retry attempt ${retryCount + 1} failed:`, error);
      
      // Update error message with retry count
      setInsights({
        error: true,
        message: `Retry attempt ${retryCount + 1} failed. ${
          error.isOverloaded 
            ? "The service is still busy." 
            : "Please try again."
        }`,
        canRetry: true
      });
      setPhase('error');
    }
    
    setIsProcessing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      submitAnswer();
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">
      {phase === 'intro' && (
        <>
          {/* Header with Back Navigation */}
          <div className="bg-white border-b-2 border-slate-900">
            <div className="max-w-5xl mx-auto px-8 py-6">
              <button 
                onClick={() => window.history.back()}
                className="flex items-center gap-2 text-slate-700 hover:text-slate-900 transition-colors mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Back to Ideas Hub</span>
              </button>
              
              <div className="flex items-start gap-6">
                <div>
                  <h1 className="text-3xl font-bold text-slate-900 mb-2">Skills Discovery Interview</h1>
                  <p className="text-base text-slate-700">Uncover hidden talents and problem-solving abilities</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-5xl mx-auto px-8 py-12">
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-base text-slate-900 leading-relaxed">
                This interview uncovers the skills, knowledge, and competencies you've developed outside the classroom - the expertise others rely on you for.
              </p>
            </div>

            {/* What We're Looking For Section */}
            <div className="mb-12 bg-white border-2 border-slate-900 rounded-xl overflow-hidden">
              <div className="px-6 py-4 bg-slate-900 border-l-4 border-l-teal-400">
                <h2 className="text-white font-bold text-sm uppercase tracking-wide">What I'll discover:</h2>
              </div>
              <div className="p-8 bg-white">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-teal-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">Skills people come to you for help with</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-teal-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">Teaching moments with adults</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-teal-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">Surprising areas of expertise</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-teal-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">Problems you've solved on your own initiative</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-teal-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">Self-taught skills and knowledge</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Callout */}
            <div className="mb-12 bg-teal-50 border-2 border-teal-500 rounded-xl p-6 border-l-4 border-l-teal-400">
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-slate-900 mb-1">Focus on what you actually DO, not what you think you're good at.</p>
                  <p className="text-xs text-slate-800 pt-2">I'm looking for specific examples of when people asked for your help, when you taught someone something, or when you solved a problem no one else could.</p>
                  <p className="text-xs font-semibold text-slate-800 pt-1">Show me the evidence; I'll find the hidden strengths.</p>
                </div>
              </div>
            </div>

            {/* Begin Button */}
            <div>
              <button 
                onClick={startInterview}
                className="px-8 py-4 bg-teal-400 text-slate-900 border-2 border-slate-900 rounded-lg font-bold text-base hover:bg-teal-500 transition-all active:scale-95 shadow-sm"
              >
                <Zap className="inline w-5 h-5 mr-3" />
                Let's Begin
              </button>
            </div>
          </div>
        </>
      )}

      {phase === 'questioning' && currentQuestion && (
        <div className="max-w-4xl mx-auto">
          {/* Progress Header */}
          <div className="bg-white border-2 border-slate-900 rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="bg-slate-900 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                  <span className="text-sm font-bold text-white">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  <span className="text-stone-400 text-xs">
                    • ~{Math.ceil((questions.length - currentQuestionIndex) * 2)} min remaining
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  {showAutoSave && (
                    <span className="text-xs text-teal-300 animate-fadeIn">Saved</span>
                  )}
                  <span className="text-sm text-stone-300">{Math.round(progress)}% complete</span>
                </div>
              </div>
            </div>
            <div className="relative h-2 bg-stone-200">
              <div 
                className="absolute inset-y-0 left-0 bg-indigo-300 transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white border border-slate-900 rounded-xl shadow-sm">
            <div className="p-8 pb-6">
              <h1 className="text-base font-semibold text-slate-900 mb-4 leading-relaxed">
                {currentQuestion.question}
              </h1>

              {/* Guidance Callout */}
              <div className="mb-6 bg-indigo-50 border-l-4 border-l-indigo-600 rounded-r-lg p-4">
                <p className="text-xs font-semibold text-indigo-900 mb-1">What makes a great response:</p>
                <p className="text-xs text-slate-900">
                  Be specific about your <span className="font-semibold text-indigo-900">skills</span> and <span className="font-semibold text-indigo-900">knowledge</span> – concrete examples and stories work best. Show how you developed these competencies and how others rely on them.
                </p>
              </div>

              {/* Textarea */}
              <div className="relative">
                <textarea
                  value={currentAnswer}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={currentQuestion.placeholder}
                  className="w-full min-h-[280px] p-4 border-2 border-slate-300 rounded-lg text-slate-900 text-sm placeholder:text-stone-500 focus:outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 transition-all resize-none"
                  autoFocus
                />
                
                {wordCount > 0 && (
                  <div className="absolute bottom-3 right-3 flex items-center gap-2">
                    {hasMinimumDetail ? (
                      <div className="flex items-center gap-1.5 text-teal-600 text-xs font-medium bg-teal-50 px-2 py-1 rounded">
                        <div className="w-1 h-1 bg-teal-600 rounded-full"></div>
                        Good detail
                      </div>
                    ) : (
                      <div className="text-stone-500 text-xs">
                        {wordCount} words • Keep going...
                      </div>
                    )}
                  </div>
                )}
              </div>

              {wordCount === 0 && (
                <button className="mt-3 text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                  Show me an example response →
                </button>
              )}
            </div>

            {/* Footer */}
            <div className="bg-stone-50 px-8 py-5 border-t border-stone-200 rounded-b-xl">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm">
                  <p className="text-stone-600 text-xs flex items-center gap-2">
                    <span className="inline-block w-1 h-1 bg-stone-400 rounded-full"></span>
                    {Object.values(responses).filter(r => r && r.trim().length > 0).length} of 4 minimum questions answered
                    <span className="inline-block w-1 h-1 bg-stone-400 rounded-full"></span>
                    Press Ctrl+Enter to submit
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <button 
                    onClick={skipQuestion}
                    className="px-5 py-2.5 text-slate-700 text-sm font-medium hover:text-slate-900 transition-colors flex items-center gap-1.5"
                  >
                    <SkipForward size={14} />
                    Skip
                  </button>
                  <button 
                    onClick={submitAnswer}
                    className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg border-2 border-slate-900 font-medium text-sm hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-2 shadow-sm"
                  >
                    {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Complete Interview'}
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              {currentQuestionIndex < questions.length - 1 && (
                <div className="pt-3 border-t border-stone-200">
                  <div className="flex items-start gap-2">
                    <div className="w-1 h-1 bg-indigo-600 rounded-full mt-1.5 flex-shrink-0"></div>
                    <div className="text-xs text-stone-600">
                      <span className="font-semibold text-slate-700">COMING NEXT:</span> {questions[currentQuestionIndex + 1].question}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {phase === 'processing' && (
        <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-lg text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-azure-500 to-indigo-500 rounded-xl flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-4">Analyzing Your Hidden Skills</h3>
          <p className="text-slate-600 mb-6">
            We're processing your responses to identify the unique talents, expertise, and problem-solving abilities that make you stand out.
          </p>
          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
            <p className="text-sm text-azure-700">
              <strong>Please wait...</strong> This analysis reveals the competencies and skills that can differentiate you from other applicants.
            </p>
          </div>
        </div>
      )}

      {phase === 'error' && insights?.error && (
        <div className="bg-red-50 rounded-2xl p-8 border border-red-200">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Analysis Generation Failed
            </h3>
            <p className="text-red-700 mb-6 text-lg">{insights.message}</p>
            
            <button
              onClick={retryAnalysis}
              disabled={isProcessing}
              className="bg-red-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <RefreshCw className={`w-5 h-5 ${isProcessing ? 'animate-spin' : ''}`} />
              {isProcessing ? 'Retrying...' : `Retry Analysis${retryCount > 0 ? ` (Attempt ${retryCount + 1})` : ''}`}
            </button>
            
            <p className="text-sm text-slate-600 mt-4">
              Your responses have been saved and will be reprocessed when you retry.
            </p>
            
            {retryCount > 2 && (
              <p className="text-sm text-amber-600 mt-2">
                If the issue persists, please try again in a few minutes or contact support.
              </p>
            )}
          </div>
        </div>
      )}

{phase === 'results' && insights && (
  <EssayResultsDisplay 
    insights={insights} 
    interviewType="hidden_competencies"
  />
)}

    </div>
  );
}